import { useState, useCallback, useMemo, createContext, useContext, useRef } from 'react'
import { CToast, CToastBody, CToaster } from '@coreui/react'

const ToastContext = createContext(null)

/**
 * Wrap your app with <ToastProvider> to enable useToast().
 *
 * Usage:
 *   const toast = useToast()
 *   toast.success('Agent deleted successfully') // acknowledged mutation, blue
 *   toast.verified('Observed state matches the requested outcome') // proof, green
 *   toast.error('Failed to connect')
 *   toast.info('Syncing playbooks...')
 */
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])
  const idRef = useRef(0)

  const addToast = useCallback((message, color = 'primary') => {
    const id = ++idRef.current
    setToasts((prev) => [...prev, { id, message, color }])
  }, [])

  // One stable value: a fresh object per render would retrigger every
  // effect that lists `toast` as a dependency (and loop any effect whose
  // failure path shows a toast). Each method stays on one line for the
  // ops-skin-contract regex.
  const toast = useMemo(() => ({
    success: (msg) => addToast(msg, 'info'),
    verified: (msg) => addToast(msg, 'success'),
    error: (msg) => addToast(msg, 'danger'),
    warning: (msg) => addToast(msg, 'warning'),
    info: (msg) => addToast(msg, 'info'),
  }), [addToast])

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <CToaster
        placement="top-end"
        className="position-fixed p-3"
        style={{ zIndex: 1080 }}
      >
        {toasts.map((t) => (
          <CToast
            key={t.id}
            autohide
            delay={4000}
            visible
            color={t.color}
            className="text-white align-items-center"
            onClose={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))}
          >
            <div className="d-flex">
              <CToastBody>{t.message}</CToastBody>
            </div>
          </CToast>
        ))}
      </CToaster>
    </ToastContext.Provider>
  )
}

export function useToast() {
  const toast = useContext(ToastContext)
  if (!toast) {
    throw new Error('useToast must be used within a <ToastProvider>')
  }
  return toast
}
